import type { ReactElement } from "react";
import { Card, CardBody } from "../../molecules/Card/index.js";
import { TrendIndicator } from "../../atoms/TrendIndicator/TrendIndicator.js";
import {
  lamaDeltaLabel,
  lamaDeltaPoints,
  lamaScorePercentLabel,
  lamaSubPointDeltaDescription,
  lamaSubPointDeltaLabel,
} from "../lama-report-presentation.js";
import { cn } from "../../lib/utils.js";

/**
 * One movers column (Most improved / Slipping) — ported from
 * `LamaReportPage`'s `MoverCard`.
 */
const TONE_CLASS: Record<"healthy" | "critical" | "neutral", string> = {
  healthy: "text-pb-green-text",
  critical: "text-pb-red-text",
  // Unchanged is not good news or bad news, so it takes the muted tone rather
  // than borrowing one of the other two (PB-191 Task 22).
  neutral: "text-pb-text-secondary",
};

export function MoverCard({
  title,
  tone,
  movers,
  emptyText,
}: {
  title: string;
  tone: "healthy" | "critical" | "neutral";
  movers: readonly { area: string; current: number; delta: number }[];
  emptyText: string;
}): ReactElement {
  return (
    <Card>
      <CardBody>
        <p className={`mb-2 text-xs font-semibold ${TONE_CLASS[tone]}`}>
          {title}
        </p>
        {movers.length === 0 ? (
          <p className="text-sm text-pb-text-muted">{emptyText}</p>
        ) : (
          <ul className="space-y-1.5">
            {movers.map((mover) => {
              /* A movement smaller than one percentage point is still a
                 movement — it just cannot be printed as one. See
                 `lamaSubPointDeltaLabel`. */
              const subPoint = lamaSubPointDeltaLabel(mover.delta);
              return (
                <li
                  key={mover.area}
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <span className="truncate text-pb-text-secondary">
                    {mover.area}
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="text-pb-text-muted pb-tabular">
                      {lamaScorePercentLabel(mover.current)}
                    </span>
                    {subPoint === null ? (
                      <TrendIndicator
                        delta={lamaDeltaPoints(mover.delta)}
                        unit=" pts"
                        aria-label={`${mover.area}: ${lamaDeltaLabel(mover.delta)}`}
                      />
                    ) : (
                      <span
                        className={cn(
                          "text-xs font-medium pb-tabular",
                          mover.delta > 0
                            ? "text-pb-green-text"
                            : "text-pb-red-text",
                        )}
                        aria-label={`${mover.area}: ${lamaSubPointDeltaDescription(mover.delta)}`}
                      >
                        {subPoint}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
